import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import classNames from 'classnames';

// Utils
import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { getListingCapabilities, projectListingForMode } from '../../util/dualListing';
import { OFFER, REQUEST } from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import { Page, NamedLink, OrderPanel, LayoutSingleColumn } from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
  getDerivedRenderData,
} from './ListingPage.shared';
import Notifications from './Notifications/Notifications';
import SectionReviews from './SectionReviews';
import SectionLender from './SectionLender';
import SectionGallery from './SectionGallery';
import SectionListingHeader, { ListingBreadcrumbs } from './SectionListingHeader';
import SectionListingDetails from './SectionListingDetails';
import SectionRentOrBuy from './SectionRentOrBuy';
import SectionSimilarListings from './SectionSimilarListings';
import ListingPageAccessWrapper from './ListingPageAccessWrapper';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  // null until the customer picks a path on a listing that offers both.
  const [orderMode, setOrderMode] = useState(null);
  const orderPanelRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const derivedData = getDerivedRenderData({
    rawParams,
    getListing,
    getOwnListing,
    showOwnListingsOnly,
    currentUser,
    config,
    intl,
    location,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
    longWordClassName: css.longWord,
    payoutDetailsWarningClassName: css.payoutDetailsWarning,
  });
  const {
    listingConfig,
    listingId,
    isVariant,
    currentListing,
    listingSlug,
    params,
    listingPathParamType,
    listingTab,
    description,
    price,
    title,
    publicData,
    metadata,
    richTitle,
    isOwnListing,
    showListingImage,
    showDescription,
    processType,
    ensuredAuthor,
    noPayoutDetailsSetWithOwnListing,
    payoutDetailsWarning,
    authorDisplayName,
    schemaTitle,
    facebookImages,
    twitterImages,
    schemaImages,
    productURL,
    availabilityMaybe,
    noIndexMaybe,
    hasInvalidListingData,
  } = derivedData;

  const gallerySection = useMemo(() => {
    return showListingImage ? (
      <div className={css.galleryArea}>
        <SectionGallery listing={currentListing} />
      </div>
    ) : null;
  }, [showListingImage, currentListing?.id?.uuid]);

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  if (hasInvalidListingData) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const unitType = publicData.unitType;
  const isNegotiation = processType === 'negotiation';

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    setInitialValues, // from ListingPage.duck.js (set initial values for the listing page)
    location,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  // The panel and its forms read process, unit and price off the listing, so
  // the buy path is expressed by giving them a listing that already reads as a
  // purchase rather than by passing a mode down through them. Checkout is handed
  // the same projection, so a Buy does not arrive there on the rental process.
  const listingForOrder = projectListingForMode(currentListing, orderMode, config.currency);

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
      const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
        ...commonParams,
        getListing,
      });
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
      const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
        ...commonParams,
        getListing,
      });
      onNavigateToRequestQuotePage(values);
    } else {
      const onSubmit = handleSubmit({
        ...commonParams,
        currentUser,
        callSetInitialValues,
        getListing,
        orderListing: listingForOrder,
        onInitializeCardPaymentData,
      });
      onSubmit(values);
    }
  };

  // A piece that has already sold cannot be bought again. Stock is only tracked
  // for listings with a purchase path, so an undefined quantity means "no stock
  // concept here", not "sold out".
  // Buy needs stock to sell. Sold out reads as 0; a listing that never had a
  // stock record reads as undefined, and neither can be bought — treating only
  // 0 as unavailable left Buy clickable on a listing with nothing behind it,
  // which then rendered an empty panel.
  const currentStockQuantity = currentListing?.currentStock?.attributes?.quantity;
  const isBuyUnavailable = !(currentStockQuantity > 0);
  console.log('isBuyUnavailable', isBuyUnavailable);
  

  // A listing offering both paths has to be asked which one first: the order
  // panel is the rent flow, so showing it up front would make "Request to book"
  // the page's default action and bury the purchase. Rent-only and buy-only
  // listings have nothing to choose, so they go straight to the panel.
  const { both: offersBoth } = getListingCapabilities(publicData);
  const showOrderPanel = !offersBoth || orderMode != null;

  // The price cards are the page's call to action; the order panel beneath them
  // is where the choice is actually made. On a single-path listing the panel is
  // already there, so the card's job is just to take you to it.
  const handleChoosePath = mode => {
    setOrderMode(mode);
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        orderPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn
        className={css.pageRootCarousel}
        topbar={topbar}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <Notifications
            mounted={mounted}
            listing={currentListing}
            isOwnListing={isOwnListing}
            noPayoutDetailsSetWithOwnListing={noPayoutDetailsSetWithOwnListing}
            currentUser={currentUser}
            className={css.actionBarForProductLayout}
            editParams={{
              id: listingId.uuid,
              slug: listingSlug,
              type: listingPathParamType,
              tab: listingTab,
            }}
          />

          <div className={css.breadcrumbRow}>
            <ListingBreadcrumbs
              title={title}
              publicData={publicData}
              categories={config.categoryConfiguration?.categories}
            />
          </div>

          <div
            className={classNames(css.layout, { [css.layoutWithoutGallery]: !showListingImage })}
          >
            <div className={css.mainArea}>
              <div className={css.titleArea}>
                <SectionListingHeader
                  title={title}
                  publicData={publicData}
                  categories={config.categoryConfiguration?.categories}
                  currency={config.currency}
                  price={currentListing?.attributes?.price}
                />
              </div>

              <div className={css.detailsArea}>
                <SectionListingDetails
                  title={title}
                  listingId={currentListing.id?.uuid}
                  description={showDescription ? description : null}
                  publicData={publicData}
                  metadata={metadata}
                  listingFieldConfigs={listingConfig.listingFields}
                  categoryConfiguration={config.categoryConfiguration}
                />
              </div>
            </div>


            <div className={css.orderArea}>
              {/* Both prices are shown up front. A listing that offers renting and
                  buying has two prices, and putting one behind the other makes the
                  cheaper daily rate read as the only price.

                  isBuyUnavailable currently covers the "already sold" case, which
                  is knowable from the listing's own stock. The brief also asks to
                  disable Buy while a rental is booked or upcoming; that needs
                  booking data this page does not load, so it is not inferred here
                  rather than guessed at from availability gaps. */}
              <SectionRentOrBuy
                className={css.rentOrBuyPanel}
                publicData={publicData}
                price={currentListing?.attributes?.price}
                currency={config.currency}
                isBuyUnavailable={isBuyUnavailable}
                selectedMode={orderMode}
                onRent={() => handleChoosePath('rent')}
                onBuy={() => handleChoosePath('buy')}
              />
              <div ref={orderPanelRef} className={css.orderPanelSlot}>
                {showOrderPanel ? (
                  <OrderPanel
                    className={classNames(css.productOrderPanel, css.productOrderPanelCarousel, {
                      [css.imagesEnabled]: showListingImage,
                    })}
                    listing={listingForOrder}
                    isOwnListing={isOwnListing}
                    onSubmit={handleOrderSubmit}
                    authorLink={
                      <NamedLink
                        className={css.authorNameLink}
                        name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                        params={params}
                        to={{ hash: '#author' }}
                      >
                        {authorDisplayName}
                      </NamedLink>
                    }
                    title={null}
                    titleDesktop={null}
                    payoutDetailsWarning={payoutDetailsWarning}
                    author={ensuredAuthor}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onContactUser={onContactUser}
                    {...restOfProps}
                    validListingTypes={config.listing.listingTypes}
                    marketplaceCurrency={config.currency}
                    dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                    marketplaceName={config.marketplaceName}
                    showListingImage={showListingImage}
                  />
                ) : null}
              </div>
              <SectionLender
                className={css.lenderCard}
                title={title}
                listing={currentListing}
                isOwnListing={isOwnListing}
                authorDisplayName={authorDisplayName}
                reviews={reviews}
                onContactUser={onContactUser}
                isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                onCloseInquiryModal={() => setInquiryModalOpen(false)}
                sendInquiryError={sendInquiryError}
                sendInquiryInProgress={sendInquiryInProgress}
                onSubmitInquiry={onSubmitInquiry}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </div>

            {gallerySection}

          </div>

          <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
          <SectionSimilarListings listing={currentListing} config={config} />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call page-specific setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const ListingPage = props => {
  const dispatch = useDispatch();
  const store = useStore();

  const { isAuthenticated } = useSelector(state => state.auth);
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = useSelector(state => state.ListingPage);
  const currentUser = useSelector(state => state.user?.currentUser);
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const getListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'listing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );
  const getOwnListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'ownListing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const callSetInitialValues = useCallback(
    (setInitialValuesFn, values, saveToSessionStorage) =>
      dispatch(setInitialValuesFn(values, saveToSessionStorage)),
    [dispatch]
  );
  const onFetchTransactionLineItems = useCallback(
    params => dispatch(fetchTransactionLineItems(params)),
    [dispatch]
  );
  const onSendInquiry = useCallback((listing, message) => dispatch(sendInquiry(listing, message)), [
    dispatch,
  ]);
  const onInitializeCardPaymentData = useCallback(() => dispatch(initializeCardPaymentData()), [
    dispatch,
  ]);
  const onFetchTimeSlots = useCallback(
    (listingId, start, end, timeZone, options) =>
      dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
    [dispatch]
  );

  return (
    <ListingPageAccessWrapper
      {...props}
      PageComponent={ListingPageComponent}
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      getListing={getListing}
      getOwnListing={getOwnListing}
      scrollingDisabled={scrollingDisabled}
      inquiryModalOpenForListingId={inquiryModalOpenForListingId}
      showListingError={showListingError}
      reviews={reviews}
      fetchReviewsError={fetchReviewsError}
      monthlyTimeSlots={monthlyTimeSlots}
      timeSlotsForDate={timeSlotsForDate}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      sendInquiryInProgress={sendInquiryInProgress}
      sendInquiryError={sendInquiryError}
      onManageDisableScrolling={onManageDisableScrolling}
      callSetInitialValues={callSetInitialValues}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      onSendInquiry={onSendInquiry}
      onInitializeCardPaymentData={onInitializeCardPaymentData}
      onFetchTimeSlots={onFetchTimeSlots}
    />
  );
};

export default ListingPage;
